import { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const isSuccess = (data) =>
  !data?.responseCode || String(data.responseCode) === "00";

const extractRows = (data) => {
  const value = data?.response ?? data?.data ?? data?.result ?? [];
  if (Array.isArray(value)) return value;
  return value && typeof value === "object" ? [value] : [];
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.responseMessage ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

export default function useBulkPackageGroupSets() {
  const { api, userID } = useContext(GlobalContext);

  const bulkCatalogIdRef = useRef("");
  const bulkCatalogPromiseRef = useRef(null);

  const resolveBulkCatalogId = async ({ force = false } = {}) => {
    const cached = String(bulkCatalogIdRef.current || "").trim();
    if (!force && cached) return cached;
    if (!force && bulkCatalogPromiseRef.current)
      return bulkCatalogPromiseRef.current;

    const request = (async () => {
      try {
        const usCode = String(userID || "").trim();
        if (!usCode) return "";

        const res = await api.post("/Inventory/getCatalogIdUSCode", { usCode });
        if (String(res?.data?.responseCode ?? "") !== "00") {
          toast.error(
            res?.data?.responseMessage ||
              "Failed to fetch catalogue mapping ID",
          );
          return "";
        }

        const response = res?.data?.response;
        const mappingId = Array.isArray(response)
          ? response?.[0]?.mapping_id
          : response?.mapping_id;
        const catalogId = String(mappingId ?? "").trim();
        if (!catalogId) {
          toast.error("Catalogue mapping ID not found");
          return "";
        }

        bulkCatalogIdRef.current = catalogId;
        return catalogId;
      } catch (error) {
        console.error(
          "getCatalogIdUSCode error:",
          error?.response?.data || error,
        );
        toast.error(
          error?.response?.data?.responseMessage ||
            error?.response?.data?.message ||
            error?.message ||
            "Error fetching catalogue mapping ID",
        );
        return "";
      } finally {
        bulkCatalogPromiseRef.current = null;
      }
    })();

    bulkCatalogPromiseRef.current = request;
    return request;
  };

  const [packageGroupSetsLoading, setPackageGroupSetsLoading] = useState(false);
  const [packageGroupSetSaving, setPackageGroupSetSaving] = useState(false);
  const [packageGroupSetDeletingId, setPackageGroupSetDeletingId] =
    useState("");

  const [packageGroupSetMembersLoading, setPackageGroupSetMembersLoading] =
    useState(false);
  const [packageGroupSetMemberSaving, setPackageGroupSetMemberSaving] =
    useState(false);
  const [
    packageGroupSetMemberDeletingKey,
    setPackageGroupSetMemberDeletingKey,
  ] = useState("");

  const getPackageGroupSets = async (packageGroupSetId = "") => {
    try {
      setPackageGroupSetsLoading(true);
      const res = await api.post("/BulkCatalogue/packageGroupSetsGet", {
        catalog_id: await resolveBulkCatalogId(),
        package_group_set_id: String(packageGroupSetId ?? "").trim(),
      });

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch package group sets",
        );
        return [];
      }

      return extractRows(res?.data);
    } catch (error) {
      console.error(
        "packageGroupSetsGet error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error fetching package group sets"));
      return [];
    } finally {
      setPackageGroupSetsLoading(false);
    }
  };

  const buildGroupSetPayload = (form = {}, includeId = false) => ({
    ...(includeId
      ? {
          package_group_set_id: String(form?.package_group_set_id ?? "").trim(),
        }
      : {}),
    package_id: String(form?.package_id ?? "").trim(),
    set_name: String(form?.set_name ?? "").trim(),
    minimum_groups_to_select: String(
      form?.minimum_groups_to_select ?? "",
    ).trim(),
    maximum_groups_to_select: String(
      form?.maximum_groups_to_select ?? "",
    ).trim(),
    display_order: String(form?.display_order ?? "").trim(),
  });

  const savePackageGroupSet = async (form, editing) => {
    if (packageGroupSetSaving) return false;

    const payload = buildGroupSetPayload(form, editing);

    if (editing && !payload.package_group_set_id) {
      toast.error("Package Group Set ID is required");
      return false;
    }
    if (!payload.package_id) {
      toast.error("Package is required");
      return false;
    }
    if (!payload.set_name) {
      toast.error("Set name is required");
      return false;
    }

    try {
      setPackageGroupSetSaving(true);
      const res = await api.post(
        editing
          ? "/BulkCatalogue/packageGroupSetsUpdate"
          : "/BulkCatalogue/packageGroupSetsInsert",
        payload,
      );

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package group set save failed",
        );
        return false;
      }

      toast.success(
        res?.data?.responseMessage ||
          `Package group set ${editing ? "updated" : "added"} successfully`,
      );
      return true;
    } catch (error) {
      console.error(
        "packageGroupSet save error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error saving package group set"));
      return false;
    } finally {
      setPackageGroupSetSaving(false);
    }
  };

  const insertPackageGroupSet = (form) => savePackageGroupSet(form, false);
  const updatePackageGroupSet = (form) => savePackageGroupSet(form, true);

  const deletePackageGroupSet = async (packageGroupSetId) => {
    const id = String(packageGroupSetId ?? "").trim();
    if (!id || packageGroupSetDeletingId) return false;

    try {
      setPackageGroupSetDeletingId(id);
      const res = await api.post("/BulkCatalogue/packageGroupSetsDelete", {
        package_group_set_id: id,
      });

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package group set delete failed",
        );
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Package group set deleted successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "packageGroupSetsDelete error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error deleting package group set"));
      return false;
    } finally {
      setPackageGroupSetDeletingId("");
    }
  };

  const getPackageGroupSetMembers = async (
    packageGroupSetId = "",
    packageOptionGroupId = "",
  ) => {
    try {
      setPackageGroupSetMembersLoading(true);
      const res = await api.post("/BulkCatalogue/packageGroupSetMembersGet", {
        catalog_id: await resolveBulkCatalogId(),
        package_group_set_id: String(packageGroupSetId ?? "").trim(),
        package_option_group_id: String(packageOptionGroupId ?? "").trim(),
      });

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage ||
            "Failed to fetch package group set members",
        );
        return [];
      }

      return extractRows(res?.data);
    } catch (error) {
      console.error(
        "packageGroupSetMembersGet error:",
        error?.response?.data || error,
      );
      toast.error(
        getErrorMessage(error, "Error fetching package group set members"),
      );
      return [];
    } finally {
      setPackageGroupSetMembersLoading(false);
    }
  };

  const insertPackageGroupSetMember = async (form = {}) => {
    if (packageGroupSetMemberSaving) return false;

    const payload = {
      package_group_set_id: String(form?.package_group_set_id ?? "").trim(),
      package_option_group_id: String(
        form?.package_option_group_id ?? "",
      ).trim(),
    };

    if (!payload.package_group_set_id || !payload.package_option_group_id) {
      toast.error("Group Set and Package Option Group are required");
      return false;
    }

    try {
      setPackageGroupSetMemberSaving(true);
      const res = await api.post(
        "/BulkCatalogue/packageGroupSetMembersInsert",
        payload,
      );

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to add group set member",
        );
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Group set member added successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "packageGroupSetMembersInsert error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error adding group set member"));
      return false;
    } finally {
      setPackageGroupSetMemberSaving(false);
    }
  };

  const updatePackageGroupSetMember = async (form = {}) => {
    if (packageGroupSetMemberSaving) return false;

    const payload = {
      package_group_set_id: String(form?.package_group_set_id ?? "").trim(),
      package_option_group_id: String(
        form?.package_option_group_id ?? "",
      ).trim(),
      new_package_group_set_id: String(
        form?.new_package_group_set_id ?? "",
      ).trim(),
      new_package_option_group_id: String(
        form?.new_package_option_group_id ?? "",
      ).trim(),
    };

    if (
      !payload.package_group_set_id ||
      !payload.package_option_group_id ||
      !payload.new_package_group_set_id ||
      !payload.new_package_option_group_id
    ) {
      toast.error("All member mapping fields are required");
      return false;
    }

    try {
      setPackageGroupSetMemberSaving(true);
      const res = await api.post(
        "/BulkCatalogue/packageGroupSetMembersUpdate",
        payload,
      );

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to update group set member",
        );
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Group set member updated successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "packageGroupSetMembersUpdate error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error updating group set member"));
      return false;
    } finally {
      setPackageGroupSetMemberSaving(false);
    }
  };

  const deletePackageGroupSetMember = async (
    packageGroupSetId,
    packageOptionGroupId,
  ) => {
    const groupSetId = String(packageGroupSetId ?? "").trim();
    const optionGroupId = String(packageOptionGroupId ?? "").trim();
    const key = `${groupSetId}:${optionGroupId}`;

    if (!groupSetId || !optionGroupId || packageGroupSetMemberDeletingKey) {
      return false;
    }

    try {
      setPackageGroupSetMemberDeletingKey(key);
      const res = await api.post(
        "/BulkCatalogue/packageGroupSetMembersDelete",
        {
          package_group_set_id: groupSetId,
          package_option_group_id: optionGroupId,
        },
      );

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to delete group set member",
        );
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Group set member deleted successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "packageGroupSetMembersDelete error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error deleting group set member"));
      return false;
    } finally {
      setPackageGroupSetMemberDeletingKey("");
    }
  };

  return {
    packageGroupSetsLoading,
    packageGroupSetSaving,
    packageGroupSetDeletingId,
    getPackageGroupSets,
    insertPackageGroupSet,
    updatePackageGroupSet,
    deletePackageGroupSet,

    packageGroupSetMembersLoading,
    packageGroupSetMemberSaving,
    packageGroupSetMemberDeletingKey,
    getPackageGroupSetMembers,
    insertPackageGroupSetMember,
    updatePackageGroupSetMember,
    deletePackageGroupSetMember,
  };
}
